import React, { useEffect, useState } from 'react';
import {
    Avatar, Button, Card, CardContent, Divider, Grid, Paper, Typography, useTheme
} from '@mui/material';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import AssignmentTurnedInIcon from '@mui/icons-material/AssignmentTurnedIn';
import ReceiptIcon from '@mui/icons-material/Receipt';
import PaymentsIcon from '@mui/icons-material/Payments';
import ListAltIcon from '@mui/icons-material/ListAlt';
import {
    LineChart, Line, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
} from 'recharts';
import { useNavigate } from 'react-router-dom';
import PageHeader from '../../../../components/page-header';
import PublicDashboardAppService from '../../../../application/platform/public/public-dashboard-app-service';
import { ISgeDashboardResponse } from '../../../../model/sge/dashboard/dashboard-response';
import { usePublicClientState, usePublicPortalBasePath } from '../../context/public-client-context';

const formatCurrency = (value: number) =>
    new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value);

const MONTH_ABBREVIATIONS_PT = ['Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun', 'Jul', 'Ago', 'Set', 'Out', 'Nov', 'Dez'];

const formatTimelinePeriod = (year: number, month: number) =>
    `${MONTH_ABBREVIATIONS_PT[month - 1] ?? month}/${year}`;

export function PublicDashboardComponent() {
    const theme = useTheme();
    const navigate = useNavigate();
    const { clientId } = usePublicClientState();
    const basePath = usePublicPortalBasePath();
    const [dashboardData, setDashboardData] = useState<ISgeDashboardResponse | null>(null);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (!clientId) return;
        setLoading(true);
        PublicDashboardAppService.getOverview(clientId)
            .then(setDashboardData)
            .finally(() => setLoading(false));
    }, [clientId]);

    const COLORS = [
        theme.palette.primary.main,
        theme.palette.success.main,
        theme.palette.warning.main,
        theme.palette.error.main,
        theme.palette.info.main,
    ];

    const CustomPieLabel = ({ cx, cy, midAngle, innerRadius, outerRadius, percent }: any) => {
        if (percent < 0.05) return null;
        const radius = innerRadius + (outerRadius - innerRadius) * 0.5;
        const x = cx + radius * Math.cos(-midAngle * Math.PI / 180);
        const y = cy + radius * Math.sin(-midAngle * Math.PI / 180);
        return (
            <text x={x} y={y} fill="white" textAnchor="middle" dominantBaseline="central" fontWeight="bold" fontSize="14">
                {`${(percent * 100).toFixed(0)}%`}
            </text>
        );
    };

    const statusDistributionData = (dashboardData?.statusDistribution ?? []).map((item, index) => ({
        ...item,
        color: COLORS[index % COLORS.length],
    }));

    const timelineData = (dashboardData?.timeline ?? []).map((item) => ({
        ...item,
        periodLabel: formatTimelinePeriod(item.year, item.month),
    }));

    const amendmentStats = dashboardData?.amendmentStatistics;
    const commitmentStats = dashboardData?.commitmentStatistics;
    const liquidationStats = dashboardData?.liquidationStatistics;
    const paymentStats = dashboardData?.paymentStatistics;

    return (
        <React.Fragment>
            <Grid item xs={12}>
                <PageHeader
                    title="Painel de Transparência"
                    subtitle="Acompanhe a execução das emendas parlamentares"
                    actions={<Button variant="contained" startIcon={<ListAltIcon />} onClick={() => navigate(`${basePath}/emendas`)}>Ver Emendas</Button>}
                />
            </Grid>

            <Grid item xs={12}>
                <Grid container spacing={3}>
                    <Grid item xs={12} sm={6} md={3}>
                        <Card sx={{ height: '100%' }}>
                            <CardContent>
                                <Grid container spacing={2} sx={{ justifyContent: 'space-between' }}>
                                    <Grid item>
                                        <Typography color="textSecondary" gutterBottom variant="overline">EMENDAS</Typography>
                                        <Typography color="textPrimary" variant="h3">{amendmentStats?.totalAmendments ?? 0}</Typography>
                                    </Grid>
                                    <Grid item>
                                        <Avatar sx={{ backgroundColor: 'primary.main', height: 56, width: 56 }}><AccountBalanceIcon /></Avatar>
                                    </Grid>
                                </Grid>
                                <Divider sx={{ my: 1.5 }} />
                                <Typography variant="caption" color="textSecondary">
                                    Valor total: {formatCurrency(amendmentStats?.totalValue ?? 0)}
                                </Typography>
                            </CardContent>
                        </Card>
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                        <Card sx={{ height: '100%' }}>
                            <CardContent>
                                <Grid container spacing={2} sx={{ justifyContent: 'space-between' }}>
                                    <Grid item>
                                        <Typography color="textSecondary" gutterBottom variant="overline">EMPENHOS</Typography>
                                        <Typography color="textPrimary" variant="h3">{commitmentStats?.totalCommitments ?? 0}</Typography>
                                    </Grid>
                                    <Grid item>
                                        <Avatar sx={{ backgroundColor: 'success.main', height: 56, width: 56 }}><AssignmentTurnedInIcon /></Avatar>
                                    </Grid>
                                </Grid>
                                <Divider sx={{ my: 1.5 }} />
                                <Typography variant="caption" color="textSecondary">
                                    Valor empenhado: {formatCurrency(commitmentStats?.totalCommittedValue ?? 0)}
                                </Typography>
                            </CardContent>
                        </Card>
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                        <Card sx={{ height: '100%' }}>
                            <CardContent>
                                <Grid container spacing={2} sx={{ justifyContent: 'space-between' }}>
                                    <Grid item>
                                        <Typography color="textSecondary" gutterBottom variant="overline">LIQUIDAÇÕES</Typography>
                                        <Typography color="textPrimary" variant="h3">{liquidationStats?.totalLiquidations ?? 0}</Typography>
                                    </Grid>
                                    <Grid item>
                                        <Avatar sx={{ backgroundColor: 'warning.main', height: 56, width: 56 }}><ReceiptIcon /></Avatar>
                                    </Grid>
                                </Grid>
                                <Divider sx={{ my: 1.5 }} />
                                <Typography variant="caption" color="textSecondary">
                                    Valor liquidado: {formatCurrency(liquidationStats?.totalLiquidatedValue ?? 0)}
                                </Typography>
                            </CardContent>
                        </Card>
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                        <Card sx={{ height: '100%' }}>
                            <CardContent>
                                <Grid container spacing={2} sx={{ justifyContent: 'space-between' }}>
                                    <Grid item>
                                        <Typography color="textSecondary" gutterBottom variant="overline">PAGAMENTOS</Typography>
                                        <Typography color="textPrimary" variant="h3">{paymentStats?.totalPayments ?? 0}</Typography>
                                    </Grid>
                                    <Grid item>
                                        <Avatar sx={{ backgroundColor: 'info.main', height: 56, width: 56 }}><PaymentsIcon /></Avatar>
                                    </Grid>
                                </Grid>
                                <Divider sx={{ my: 1.5 }} />
                                <Typography variant="caption" color="textSecondary">
                                    Valor pago: {formatCurrency(paymentStats?.totalPaidValue ?? 0)}
                                </Typography>
                            </CardContent>
                        </Card>
                    </Grid>
                </Grid>
            </Grid>

            <Grid item xs={12} md={7}>
                <Card>
                    <CardContent>
                        <Typography variant="h6" gutterBottom>Linha do Tempo — Emendas e Empenhos</Typography>
                        <Divider sx={{ mb: 2 }} />
                        {timelineData.length > 0 ? (
                            <ResponsiveContainer width="100%" height={300}>
                                <LineChart data={timelineData} margin={{ top: 5, right: 20, left: 0, bottom: 5 }}>
                                    <CartesianGrid strokeDasharray="3 3" />
                                    <XAxis dataKey="periodLabel" style={{ fontSize: '12px' }} />
                                    <YAxis style={{ fontSize: '12px' }} allowDecimals={false} />
                                    <Tooltip content={({ active, payload }) => {
                                        if (active && payload && payload.length) {
                                            return (
                                                <Paper sx={{ p: 1.5 }}>
                                                    <Typography variant="body2" fontWeight="bold">{payload[0].payload.periodLabel}</Typography>
                                                    {payload.map((entry: any, i: number) => (
                                                        <Typography key={i} variant="body2" sx={{ color: entry.color }}>
                                                            {entry.name}: {entry.value}
                                                        </Typography>
                                                    ))}
                                                </Paper>
                                            );
                                        }
                                        return null;
                                    }} />
                                    <Legend />
                                    <Line type="monotone" dataKey="amendmentCount" stroke={theme.palette.primary.main} strokeWidth={2} dot={{ r: 4 }} name="Emendas" />
                                    <Line type="monotone" dataKey="commitmentCount" stroke={theme.palette.success.main} strokeWidth={2} dot={{ r: 4 }} name="Empenhos" />
                                </LineChart>
                            </ResponsiveContainer>
                        ) : (
                            <Typography variant="body2" color="textSecondary" align="center" sx={{ py: 5 }}>
                                {loading ? 'Carregando...' : 'Sem dados para o período'}
                            </Typography>
                        )}
                    </CardContent>
                </Card>
            </Grid>

            <Grid item xs={12} md={5}>
                <Card sx={{ height: '100%' }}>
                    <CardContent>
                        <Typography variant="h6" gutterBottom>Distribuição por Status</Typography>
                        <Divider sx={{ mb: 2 }} />
                        {statusDistributionData.length > 0 ? (
                            <ResponsiveContainer width="100%" height={300}>
                                <PieChart>
                                    <Pie data={statusDistributionData} cx="50%" cy="50%" labelLine={false} label={CustomPieLabel} outerRadius={100} dataKey="count">
                                        {statusDistributionData.map((entry, index) => <Cell key={`cell-${index}`} fill={entry.color} />)}
                                    </Pie>
                                    <Tooltip content={({ active, payload }) => {
                                        if (active && payload && payload.length) {
                                            const item = payload[0].payload;
                                            return (
                                                <Paper sx={{ p: 1.5 }}>
                                                    <Typography variant="body2" fontWeight="bold">{item.name}</Typography>
                                                    <Typography variant="body2">Quantidade: {item.count}</Typography>
                                                    <Typography variant="body2">Percentual: {item.percentage.toFixed(1)}%</Typography>
                                                </Paper>
                                            );
                                        }
                                        return null;
                                    }} />
                                    <Legend verticalAlign="bottom" height={36} formatter={(value, entry: any) => `${value}: ${entry.payload.count}`} />
                                </PieChart>
                            </ResponsiveContainer>
                        ) : (
                            <Typography variant="body2" color="textSecondary" align="center" sx={{ py: 5 }}>
                                {loading ? 'Carregando...' : 'Sem emendas cadastradas'}
                            </Typography>
                        )}
                    </CardContent>
                </Card>
            </Grid>
        </React.Fragment>
    );
}
